import { createReducer } from 'typesafe-actions';
import { TimerState, sessionDetails } from './types';
import { TimerStatus } from 'shovel-lib/types';
import * as actions from './actions';
import * as calendarActions from '../calendar/actions';
import * as taskActions from '../task/actions';
import { toMomentDate } from 'shovel-lib/utils/timeUtils';

const initialState: TimerState = {
  loading: false,
  error: '',
  status: TimerStatus.INITIAL,
  startedInUtc: null,
  timePassedInSeconds: 0
};

const reducer = createReducer(initialState)
  .handleAction(actions.start.request, state => ({ ...state, loading: true, error: '' }))
  .handleAction(actions.start.success, (state, action) => {
    const { timerDto, taskId } = action.payload;
    return {
      ...state,
      loading: false,
      startedInUtc: timerDto.startedInUtc,
      status: timerDto.state,
      title: timerDto.title,
      plannedTaskId: timerDto.uid,
      masterTaskId: taskId,
      timePassedInSeconds: timerDto.timePassedInSeconds || 0,
      ...sessionDetails(timerDto as any)
    };
  })
  .handleAction(actions.start.failure, (state, action) => ({ ...state, loading: false, error: action.payload }))
  .handleAction(actions.toggle.request, state => ({ ...state, loading: true, error: '' }))
  .handleAction(actions.toggle.success, (state, action) => ({
    ...state,
    loading: false,
    startedInUtc: action.payload.startedInUtc,
    status: action.payload.state,
    timePassedInSeconds: action.payload.timePassedInSeconds
  }))
  .handleAction(actions.toggle.failure, (state, action) => ({ ...state, loading: false, error: action.payload }))
  .handleAction(actions.cancel.request, state => ({ ...state, loading: true, error: '' }))
  .handleAction(actions.cancel.success, (state, action) => ({
    ...state,
    loading: false,
    startedInUtc: action.payload.response.startedInUtc,
    status: action.payload.response.state,
    timePassedInSeconds: action.payload.response.timePassedInSeconds
  }))
  .handleAction(actions.cancel.failure, (state, action) => ({ ...state, loading: false, error: action.payload }))
  .handleAction(actions.getCurrentTimer.request, state => ({ ...state, loading: true, error: '' }))
  .handleAction(actions.getCurrentTimer.success, (state, action) => {
    const { timePassedInSeconds, startedInUtc, title } = action.payload;
    return {
      ...state,
      title,
      loading: false,
      startedInUtc: startedInUtc,
      timePassedInSeconds: timePassedInSeconds,
      status: action.payload.state,
      masterTaskId: Number(action.payload.metadata) || undefined,
      plannedTaskId: action.payload.uid,
      ...sessionDetails(action.payload as any)
    };
  })
  .handleAction(actions.getCurrentTimer.failure, state => ({ ...state, loading: false }))
  .handleAction(actions.updateTimerForTask.success, (state, action) => {
    const { timePassedInSeconds, startedInUtc } = action.payload;
    return {
      ...state,
      startedInUtc,
      timePassedInSeconds,
      loading: false
    };
  })
  // The session's plan changed while its timer runs (its block resized, or Time planned typed in its popup): the
  // countdown and the pill's "35m done" follow it, rather than the plan the timer started with (Petr, 2026-10-15: the
  // popup said 35m, the pill asked at 55m).
  .handleAction(calendarActions.resizePlannedTask.success, (state, action) => {
    const { newEvent } = action.payload;
    if (!state.plannedTaskId || Number(newEvent?.id) !== Number(state.plannedTaskId) || state.sessionIsUsed) return state;
    const minutes = toMomentDate(newEvent.end).diff(toMomentDate(newEvent.start), 'minutes');
    return minutes > 0 ? { ...state, plannedMinutes: minutes } : state;
  })
  .handleAction(calendarActions.updatePlannedTask.success, (state, action) => {
    const dto: any = action.payload.data?.plannedTaskDto;
    if (!state.plannedTaskId || !dto || Number(dto.id) !== Number(state.plannedTaskId) || !dto.plannedMinutes) return state;
    return { ...state, plannedMinutes: dto.plannedMinutes };
  })
  // Its task renamed (its details, the Task List, the session's popup) while the timer runs: the pill says the new name
  // at once, rather than the one from when the page loaded (Petr, 2026-10-31).
  .handleAction(taskActions.saveManageTask.success, (state, action) => {
    const title = action.payload.request?.title;
    if (!title || !state.masterTaskId || Number(action.payload.taskId) !== Number(state.masterTaskId)) return state;
    return { ...state, title };
  })
  .handleAction(actions.clearSession, () => initialState);

export default reducer;
